import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { changePassword, getSiteSettings, updateSiteSettings } from '../lib/api'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { PageHeader } from '../components/ui/PageHeader'
import { Spinner } from '../components/ui/Spinner'

// ── Schemas ──────────────────────────────────────────────────────────────────

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password'),
  newPassword:     z.string().min(6, 'At least 6 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your new password'),
}).refine(d => d.newPassword === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

const socialSchema = z.object({
  whatsApp:  z.string().max(500).optional().or(z.literal('')),
  telegram:  z.string().max(500).optional().or(z.literal('')),
  instagram: z.string().max(500).optional().or(z.literal('')),
  facebook:  z.string().max(500).optional().or(z.literal('')),
  youTube:   z.string().max(500).optional().or(z.literal('')),
  twitter:   z.string().max(500).optional().or(z.literal('')),
  email:     z.string().max(200).optional().or(z.literal('')),
  phone:     z.string().max(50).optional().or(z.literal('')),
  address:   z.string().max(500).optional().or(z.literal('')),
})

// ── Social field definitions ─────────────────────────────────────────────────

const SOCIAL_FIELDS = [
  { name: 'whatsApp',  label: 'WhatsApp',  icon: 'ti-brand-whatsapp',  color: 'text-emerald-500', placeholder: 'https://wa.me/905551234567' },
  { name: 'telegram',  label: 'Telegram',  icon: 'ti-brand-telegram',  color: 'text-sky-500',     placeholder: 'https://t.me/yourhandle' },
  { name: 'instagram', label: 'Instagram', icon: 'ti-brand-instagram', color: 'text-pink-500',    placeholder: 'https://instagram.com/yourhandle' },
  { name: 'facebook',  label: 'Facebook',  icon: 'ti-brand-facebook',  color: 'text-blue-500',    placeholder: 'https://facebook.com/yourpage' },
  { name: 'youTube',   label: 'YouTube',   icon: 'ti-brand-youtube',   color: 'text-red-500',     placeholder: 'https://youtube.com/@yourchannel' },
  { name: 'twitter',   label: 'X / Twitter', icon: 'ti-brand-x',       color: 'text-gray-700',    placeholder: 'https://x.com/yourhandle' },
]

const CONTACT_FIELDS = [
  { name: 'email',   label: 'Email',   icon: 'ti-mail',    placeholder: 'hello@afghanspot.com' },
  { name: 'phone',   label: 'Phone',   icon: 'ti-phone',   placeholder: '+49 30 1234 5678' },
  { name: 'address', label: 'Address', icon: 'ti-map-pin', placeholder: 'Sonnenallee 78, Berlin' },
]

// ── Password form ─────────────────────────────────────────────────────────────

function PasswordSection() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(passwordSchema),
  })

  const mutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }) => changePassword({ currentPassword, newPassword }),
    onSuccess: () => { toast.success('Password updated'); reset() },
    onError:   (e) => toast.error(e.message),
  })

  return (
    <Card>
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
          <i className="ti ti-lock text-indigo-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">Change Password</p>
          <p className="text-xs text-gray-400">Use a strong password to keep your account secure</p>
        </div>
      </div>
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">
        <Input label="Current Password" type="password" placeholder="••••••••"
          error={errors.currentPassword?.message} {...register('currentPassword')} />
        <Input label="New Password" type="password" placeholder="At least 6 characters"
          hint="Use uppercase letters, numbers and special characters"
          error={errors.newPassword?.message} {...register('newPassword')} />
        <Input label="Confirm New Password" type="password" placeholder="••••••••"
          error={errors.confirmPassword?.message} {...register('confirmPassword')} />
        <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
          <i className="ti ti-check" /> Update Password
        </Button>
      </form>
    </Card>
  )
}

// ── Social + Contact form ─────────────────────────────────────────────────────

function SocialSection() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['site-settings'],
    queryFn:  getSiteSettings,
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(socialSchema),
    defaultValues: {
      whatsApp: '', telegram: '', instagram: '', facebook: '',
      youTube: '', twitter: '', email: '', phone: '', address: '',
    },
  })

  // Populate form when data arrives
  useEffect(() => {
    if (data) {
      reset({
        whatsApp:  data.whatsApp  ?? '',
        telegram:  data.telegram  ?? '',
        instagram: data.instagram ?? '',
        facebook:  data.facebook  ?? '',
        youTube:   data.youTube   ?? '',
        twitter:   data.twitter   ?? '',
        email:     data.email     ?? '',
        phone:     data.phone     ?? '',
        address:   data.address   ?? '',
      })
    }
  }, [data, reset])

  const mutation = useMutation({
    mutationFn: updateSiteSettings,
    onSuccess: () => {
      qc.invalidateQueries(['site-settings'])
      toast.success('Settings saved')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isLoading) return (
    <Card>
      <div className="flex justify-center py-8"><Spinner /></div>
    </Card>
  )

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
          <i className="ti ti-share text-emerald-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">Social Media & Contact</p>
          <p className="text-xs text-gray-400">Shown in the footer and contact page of the public site</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-8">

        {/* Social media */}
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Social Media</p>
          <div className="divide-y divide-gray-50 rounded-xl border border-gray-100 overflow-hidden">
            {SOCIAL_FIELDS.map(f => (
              <div key={f.name} className="flex items-center gap-0">
                {/* Icon badge */}
                <div className="flex w-12 h-12 items-center justify-center flex-shrink-0 bg-gray-50 border-r border-gray-100">
                  <i className={`ti ${f.icon} text-lg ${f.color}`} />
                </div>
                {/* Label */}
                <span className="w-24 flex-shrink-0 pl-3 text-xs font-medium text-gray-500">{f.label}</span>
                {/* Input */}
                <input
                  type="url"
                  placeholder={f.placeholder}
                  className="flex-1 h-12 px-3 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:bg-indigo-50/30 transition-colors"
                  {...register(f.name)}
                />
              </div>
            ))}
          </div>
          {Object.keys(errors).some(k => SOCIAL_FIELDS.find(f => f.name === k)) && (
            <p className="mt-1 text-xs text-red-500">Please enter valid URLs</p>
          )}
        </div>

        {/* Contact info */}
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Contact Information</p>
          <div className="divide-y divide-gray-50 rounded-xl border border-gray-100 overflow-hidden">
            {CONTACT_FIELDS.map(f => (
              <div key={f.name} className="flex items-center gap-0">
                <div className="flex w-12 h-12 items-center justify-center flex-shrink-0 bg-gray-50 border-r border-gray-100">
                  <i className={`ti ${f.icon} text-base text-gray-400`} />
                </div>
                <span className="w-24 flex-shrink-0 pl-3 text-xs font-medium text-gray-500">{f.label}</span>
                <input
                  type={f.name === 'email' ? 'email' : 'text'}
                  placeholder={f.placeholder}
                  className="flex-1 h-12 px-3 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:bg-indigo-50/30 transition-colors"
                  {...register(f.name)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            <i className="ti ti-device-floppy" /> Save Settings
          </Button>
        </div>
      </form>
    </Card>
  )
}

// ── System info card ──────────────────────────────────────────────────────────

function IconGuide() {
  const EXAMPLES = [
    { icon: 'ti-building-store', label: 'building-store' },
    { icon: 'ti-tools-kitchen-2', label: 'tools-kitchen-2' },
    { icon: 'ti-shopping-bag', label: 'shopping-bag' },
    { icon: 'ti-scissors', label: 'scissors' },
    { icon: 'ti-map-pin', label: 'map-pin' },
    { icon: 'ti-star', label: 'star' },
    { icon: 'ti-phone', label: 'phone' },
    { icon: 'ti-diamond', label: 'diamond' },
    { icon: 'ti-bread', label: 'bread' },
    { icon: 'ti-device-laptop', label: 'device-laptop' },
    { icon: 'ti-shirt', label: 'shirt' },
    { icon: 'ti-car', label: 'car' },
  ]

  return (
    <Card>
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
            <i className="ti ti-icons text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">Icon Library</p>
            <p className="text-xs text-gray-400">Tabler Icons — 5000+ icons</p>
          </div>
        </div>
        <a
          href="https://tabler.io/icons"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-600 hover:bg-violet-100 transition-colors"
        >
          <i className="ti ti-external-link text-xs" />
          Browse all icons
        </a>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Enter the icon name starting with <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">ti-</code> in icon fields.
        Example: <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">ti-building-store</code>
      </p>

      <div className="grid grid-cols-4 gap-2">
        {EXAMPLES.map(({ icon, label }) => (
          <button
            key={icon}
            type="button"
            onClick={() => navigator.clipboard.writeText(icon).then(() => {})}
            title={`Copy: ${icon}`}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-gray-100 bg-gray-50 p-3 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600 transition-colors group"
          >
            <i className={`ti ${icon} text-xl text-gray-500 group-hover:text-violet-600`} />
            <span className="text-[10px] text-gray-400 group-hover:text-violet-500 leading-tight text-center truncate w-full">{label}</span>
          </button>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-gray-400 text-center">Click any icon → class name is copied</p>
    </Card>
  )
}

function SystemInfo() {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
        <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center">
          <i className="ti ti-info-circle text-slate-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">System Info</p>
          <p className="text-xs text-gray-400">Application version and environment</p>
        </div>
      </div>
      <div className="space-y-3 text-sm">
        {[
          { label: 'Application', value: 'AfghanSpot Admin' },
          { label: 'Version',     value: 'v1.0.0'           },
          { label: 'API',         value: process.env.REACT_APP_API_URL || '—' },
          { label: 'Environment', value: process.env.NODE_ENV },
        ].map(item => (
          <div key={item.label} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
            <span className="text-gray-500">{item.label}</span>
            <span className="font-medium text-gray-900">{item.value}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Settings() {
  return (
    <div>
      <PageHeader title="Settings" description="Account security and site configuration" />

      <div className="space-y-6">
        {/* Row 1 — Social + Password side by side on large screens */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <SocialSection />
          <div className="space-y-6">
            <PasswordSection />
            <IconGuide />
            <SystemInfo />
          </div>
        </div>
      </div>
    </div>
  )
}
